import { useEffect, useState, useMemo } from 'react';
import { Camera, Images } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@utils/api';
import UploadPhotosDialog, { normalizePhotoSrc } from '@components/memories/UploadPhotosDialog';

/** Hide check-in UI until vibe start was this far in the past (match server AFTER_LO slack). */
const FOLLOWUP_AFTER_MS = 60 * 1000;

const ActivityMemoryPanel = ({
  activityId,
  activityTitle,
  activityDateTime,
  activityCancelled,
  isParticipant,
  user,
  siteOrigin,
  memorySectionRef,
}) => {
  const [memory, setMemory] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [happened, setHappened] = useState(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [shareWithEveryone, setShareWithEveryone] = useState(null);
  const [yaarviPromo, setYaarviPromo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const showPanel = Boolean(
    user
    && isParticipant
    && activityDateTime
    && !activityCancelled
    && Date.now() - new Date(activityDateTime).getTime() >= FOLLOWUP_AFTER_MS
  );

  const refresh = async () => {
    try {
      const [memRes, photosRes] = await Promise.all([
        api.get(`/activities/${activityId}/memory`),
        api.get(`/memories/activities/${activityId}/photos`).catch(() => ({ data: { photos: [] } })),
      ]);
      setMemory(memRes.data.memory || null);
      setAlbumPhotos(photosRes.data.photos || []);
    } catch {
      setMemory(null);
    }
  };

  useEffect(() => {
    let revoke;
    if (file) {
      const obj = URL.createObjectURL(file);
      setPreviewUrl(obj);
      revoke = obj;
    } else {
      setPreviewUrl('');
    }
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [file]);

  useEffect(() => {
    if (!showPanel || !user) {
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPanel, user, activityId]);

  const displayPhotoSrc = useMemo(() => {
    if (previewUrl) return previewUrl;
    if (albumPhotos[0]?.url) return normalizePhotoSrc(albumPhotos[0].url, siteOrigin || '');
    if (!memory?.photoUrl) return '';
    return normalizePhotoSrc(memory.photoUrl, siteOrigin || '');
  }, [previewUrl, memory?.photoUrl, albumPhotos, siteOrigin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (happened !== true && happened !== false) return;

    try {
      setSubmitting(true);

      if (happened && file) {
        if (shareWithEveryone !== true && shareWithEveryone !== false) {
          toast.error('Choose whether to share with everyone');
          setSubmitting(false);
          return;
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('activityId', activityId);
        const { data } = await api.post('/aws/upload-activity-memory', fd);
        const photoUrl = (data.fileUrl || '').trim();
        if (!photoUrl) throw new Error('Upload failed');

        await api.post(`/memories/activities/${activityId}/photos`, {
          urls: [photoUrl],
          shareWithParticipants: shareWithEveryone,
          shareScope: yaarviPromo ? 'yaarvi_promo' : 'private_only',
        });
      } else {
        await api.post(`/activities/${activityId}/memory`, {
          happened,
        });
      }

      await refresh();
      setHappened(null);
      setFile(null);
      setShareWithEveryone(null);
      setYaarviPromo(false);
      toast.success('Saved. Thanks for checking in.');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not save';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!showPanel || !loaded) return null;

  const titleSnippet = activityTitle || 'This vibe';
  const hasPhotos = albumPhotos.length > 0 || Boolean(memory?.photoUrl);

  if (memory) {
    const scopeLabel =
      memory.shareScope === 'yaarvi_promo'
        ? 'You allowed Yaarvi to feature this photo on Instagram and our pages.'
        : memory.photoUrl && memory.shareScope === 'private_only'
          ? 'Private — shown only to you here.'
          : '';

    return (
      <>
        <section
          ref={memorySectionRef}
          id="activity-memory"
          className="card scroll-mt-24 rounded-2xl border border-teal-200/70 bg-teal-50/60 p-4 dark:border-teal-500/25 dark:bg-teal-950/25 sm:scroll-mt-28"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm dark:bg-teal-900/40">
              <Camera size={22} className="text-teal-600 dark:text-teal-400" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200">
                How it went
              </h3>
              <p className="mt-1 text-sm font-medium text-surface-900 dark:text-surface-100">
                {memory.happened
                  ? `You said "${titleSnippet}" happened. Thanks for checking in.`
                  : `You said "${titleSnippet}" didn’t quite happen.`}
              </p>
              {hasPhotos ? (
                <div className="mt-3">
                  {albumPhotos.length > 1 ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      {albumPhotos.slice(0, 6).map((p) => (
                        <img
                          key={p._id}
                          src={normalizePhotoSrc(p.url, siteOrigin || '')}
                          alt=""
                          className="aspect-square w-full rounded-lg object-cover ring-1 ring-black/10 dark:ring-white/10"
                        />
                      ))}
                    </div>
                  ) : (
                    <img
                      src={displayPhotoSrc}
                      alt=""
                      className="max-h-48 w-full rounded-xl object-cover ring-1 ring-black/10 dark:ring-white/10"
                    />
                  )}
                  {scopeLabel ? (
                    <p className="mt-2 text-xs text-surface-600 dark:text-surface-400">{scopeLabel}</p>
                  ) : null}
                </div>
              ) : null}

              {memory.happened ? (
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-500 dark:bg-teal-500 dark:hover:bg-teal-400"
                >
                  <Images size={18} />
                  Upload Photos
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <UploadPhotosDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          activityId={activityId}
          onUploaded={refresh}
        />
      </>
    );
  }

  return (
    <section
      ref={memorySectionRef}
      id="activity-memory"
      className="card scroll-mt-24 rounded-2xl border border-teal-200/70 bg-teal-50/60 p-4 dark:border-teal-500/25 dark:bg-teal-950/25 sm:scroll-mt-28"
    >
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200">
        Quick check-in
      </h3>
      <p className="text-sm text-surface-700 dark:text-surface-300">
        Did <span className="font-semibold text-surface-900 dark:text-white">{titleSnippet}</span>{' '}
        happen?
      </p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setHappened(true);
            }}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
              happened === true
                ? 'bg-teal-600 text-white shadow-sm dark:bg-teal-500'
                : 'bg-white text-teal-800 ring-1 ring-teal-200 dark:bg-teal-900/30 dark:text-teal-100 dark:ring-teal-500/30'
            }`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => {
              setHappened(false);
              setFile(null);
            }}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
              happened === false
                ? 'bg-surface-700 text-white dark:bg-surface-600'
                : 'bg-white text-surface-700 ring-1 ring-surface-200 dark:bg-surface-800 dark:text-surface-200 dark:ring-surface-600'
            }`}
          >
            Not really
          </button>
        </div>

        {happened === true ? (
          <div className="space-y-3 rounded-xl bg-white/80 p-3 dark:bg-surface-900/40">
            <label className="block text-xs font-medium text-surface-600 dark:text-surface-400">
              Add photos (optional)
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-teal-300/80 bg-teal-50/50 px-3 py-3 dark:border-teal-500/30 dark:bg-teal-950/30">
              <Camera size={20} className="shrink-0 text-teal-600 dark:text-teal-400" />
              <span className="text-sm text-teal-900 dark:text-teal-100">
                {file ? file.name : 'Tap to choose an image'}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(ev) => {
                  const next = ev.target.files?.[0];
                  setFile(next || null);
                }}
              />
            </label>
            {displayPhotoSrc ? (
              <img src={displayPhotoSrc} alt="" className="max-h-44 w-full rounded-lg object-cover" />
            ) : null}

            {file ? (
              <fieldset className="space-y-2">
                <legend className="text-xs font-semibold text-surface-700 dark:text-surface-300">
                  Share these photos with everyone who joined this activity?
                </legend>
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="shareEveryone"
                    className="mt-1"
                    checked={shareWithEveryone === true}
                    onChange={() => setShareWithEveryone(true)}
                  />
                  <span>
                    <span className="font-medium text-surface-900 dark:text-white">Yes</span>
                    {shareWithEveryone === true && (
                      <span className="mt-0.5 block text-xs text-surface-500 dark:text-surface-400">
                        These photos will be available for every participant if they accept.
                      </span>
                    )}
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="shareEveryone"
                    className="mt-1"
                    checked={shareWithEveryone === false}
                    onChange={() => setShareWithEveryone(false)}
                  />
                  <span className="font-medium text-surface-900 dark:text-white">No — keep private</span>
                </label>
                <label className="flex cursor-pointer items-start gap-2 border-t border-surface-200/50 pt-2 text-sm dark:border-surface-700/50">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={yaarviPromo}
                    onChange={(e) => setYaarviPromo(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium text-surface-900 dark:text-white">
                      Yaarvi may post it
                    </span>
                    <span className="block text-xs text-surface-500 dark:text-surface-400">
                      You’re okay with us featuring it on Instagram and our landing pages.
                    </span>
                  </span>
                </label>
              </fieldset>
            ) : null}
          </div>
        ) : null}

        {happened === true || happened === false ? (
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full !py-3 !text-[0.95rem]"
          >
            {submitting ? 'Saving…' : 'Submit'}
          </button>
        ) : null}
      </form>
    </section>
  );
};

export default ActivityMemoryPanel;
